import { FeedbackLine } from "../ui/FeedbackLine";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  Box,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  useChecks,
  useCreateRun,
  useMaintenanceJobs,
  useMarkRegression,
  useRegressions,
} from "../api/maintenance";
import { useSettings } from "../api/admin";
import { ApiError } from "../api/client";
import { nativeRuntime } from "../platform/native";
import { handleNativeLink } from "../platform/nativeLinks";
import { msToZonedText } from "../time/parse";
import { useZone } from "../time/useZone";
import { MONO } from "./MaintenanceRun";
import { Chip } from "../ui/Chip";
import { FieldDiff } from "../ui/FieldDiff";
import { StatePill } from "../ui/StatePill";

export function Repairs({
  blocked,
  onStarted,
}: {
  blocked: string | null;
  onStarted: (id: number) => void;
}) {
  const { data: jobs } = useMaintenanceJobs();
  const create = useCreateRun();
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation("admin");

  async function start(kind: string) {
    setError(null);
    try {
      const run = await create.mutateAsync({ kind });
      onStarted(run.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  // Two kinds need a subject before they mean anything, so neither gets a bare button here.
  // `preview_markers` has its own control below; `undo` is reached from the run it puts back,
  // which is the only place the target is known — started from a list it can only ask for the
  // id of a run nobody has named yet, and be refused for saying so.
  const startable = (jobs ?? []).filter(
    (job) => job.kind !== "preview_markers" && job.kind !== "undo",
  );

  return (
    <Stack gap="sm" mb={26} maw={900}>
      <Title order={3} fz="sm" lh={1.5} mb={4}>
        {t("maintenancePage.repairsHeading")}
      </Title>
      <Stack gap={8} maw={900}>
        {startable.map((job) => (
          <Paper
            key={job.kind}
            px={12}
            py={10}
            display="flex"
            style={{
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <div>
              <Text fz={13}>{job.label}</Text>
              <Text fz={12} c="dimmed" mt={2}>
                {job.applicable
                  ? t("maintenancePage.jobApplicable")
                  : t("maintenancePage.jobReadOnly")}
              </Text>
            </div>
            <Chip
              disabled={blocked !== null || create.isPending}
              onClick={() => void start(job.kind)}
            >
              {create.isPending
                ? t("maintenancePage.starting")
                : job.applicable
                  ? t("maintenancePage.preview")
                  : t("maintenancePage.check")}
            </Chip>
          </Paper>
        ))}
      </Stack>
      <MarkerPreview blocked={blocked} onStarted={onStarted} />
      <FeedbackLine role="alert">{error}</FeedbackLine>
    </Stack>
  );
}

/**
 * Try a candidate live-marker list against the archive before saving it.
 *
 * The marker list is editable from Settings today with nothing to say what it would do. It
 * feeds `parse_live_title`, whose key the segmenter groups consecutive titles by — so a
 * marker change that moves a key re-cuts shows that are already in the archive, which is the
 * one thing the parser's own contract says must never happen quietly. This is where that
 * gets said out loud, before the setting is saved rather than after.
 */
function MarkerPreview({
  blocked,
  onStarted,
}: {
  blocked: string | null;
  onStarted: (id: number) => void;
}) {
  const { data: settings } = useSettings();
  const create = useCreateRun();
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation("admin");

  const current = settings?.find((setting) => setting.key === "live_markers");
  const currentText = Array.isArray(current?.value) ? current.value.join(", ") : "";
  const text = draft ?? currentText;

  async function preview() {
    setError(null);
    const markers = text
      .split(",")
      .map((marker) => marker.trim())
      .filter(Boolean);
    try {
      const run = await create.mutateAsync({ kind: "preview_markers", params: { markers } });
      onStarted(run.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  return (
    <Paper mt={12} px={12} py={10}>
      <Text component="label" htmlFor="marker-draft" fz={13}>
        {t("maintenancePage.markersLabel")}
      </Text>
      <Text fz={12} c="dimmed" mt={2}>
        {t("maintenancePage.markersBlurb")}
      </Text>
      <Group gap={6}>
        <TextInput
          id="marker-draft"
          style={{ flex: "1 1 220px", minWidth: 0 }}
          value={text}
          onChange={(event) => setDraft(event.currentTarget.value)}
          placeholder={t("maintenancePage.markersPlaceholder")}
        />
        <Chip
          disabled={blocked !== null || create.isPending || !text.trim()}
          onClick={() => void preview()}
        >
          {create.isPending ? t("maintenancePage.checking") : t("maintenancePage.check")}
        </Chip>
      </Group>
      <FeedbackLine role="alert">{error}</FeedbackLine>
    </Paper>
  );
}

/**
 * Where the parser still reads a title differently from the person who corrected it.
 *
 * Every hand-corrected row is somebody saying the parser got that title wrong, and the
 * re-label deliberately never touches those rows — so the archive accumulates ground truth
 * it has no way to learn from. This is that ground truth, read back and rulable on.
 *
 * Confirming promotes a row into a corpus you download and commit; from then on a parser
 * change that breaks it fails the build instead of surfacing months later as a wrong country
 * on a show page. Dismissing records that the edit was about something else — an encoder
 * typo, a stand-in DJ — which is what stops this becoming a list nobody can ever clear.
 */
export function ParserBoard() {
  const [includeDismissed, { toggle: toggleDismissed }] = useDisclosure(false);
  const { data } = useRegressions(includeDismissed);
  const mark = useMarkRegression();
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation("admin");
  const runtime = nativeRuntime();

  if (!data) return null;

  async function rule(segmentId: number, verdict: string) {
    setError(null);
    try {
      await mark.mutateAsync({ segment_id: segmentId, verdict });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  return (
    <Stack gap="sm" mb={26} maw={900}>
      <Title order={3} fz="sm" lh={1.5} mb={4}>
        {t("maintenancePage.parserHeading")}
      </Title>
      <Text size="xs" c="dimmed" maw="70ch" mb={14}>
        {t("maintenancePage.parserBlurb", {
          count: data.corrected_total,
          agreed: data.agreed,
        })}
      </Text>

      <Group gap={6} mt={12}>
        <Chip pressed={includeDismissed} onClick={toggleDismissed}>
          {t("maintenancePage.showDismissed")}
        </Chip>
        <Chip
          as="link"
          href="/api/maintenance/parser/corpus"
          onClick={(event) =>
            void handleNativeLink(event, runtime, (native) =>
              native.saveFile("/api/maintenance/parser/corpus"),
            )
          }
        >
          {t("maintenancePage.downloadCorpus")}
        </Chip>
      </Group>

      {data.rows.length === 0 ? (
        <Text fz={12} mt={6} c="dimmed">
          {t("maintenancePage.parserNothing")}
        </Text>
      ) : (
        <Stack gap={6} mt={14}>
          {data.rows.map((row) => (
            <Paper key={row.segment_id}>
              <Group justify="space-between" gap={12} px={12} py={8}>
                <Text
                  component="code"
                  display="block"
                  ff={MONO}
                  fz={11}
                  c="dimmed"
                  my={8}
                  style={{ flex: "1 1 240px", minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {row.raw_title}
                </Text>
                <Group gap={6}>
                  {row.verdict && (
                    <StatePill tone="quiet">
                      {t(`maintenancePage.verdict.${row.verdict}`, { defaultValue: row.verdict })}
                    </StatePill>
                  )}
                  <Chip
                    disabled={mark.isPending}
                    title={t("maintenancePage.confirmTitle")}
                    onClick={() => void rule(row.segment_id, "confirmed")}
                  >
                    {t("maintenancePage.confirm")}
                  </Chip>
                  <Chip
                    disabled={mark.isPending}
                    title={t("maintenancePage.dismissTitle")}
                    onClick={() => void rule(row.segment_id, "dismissed")}
                  >
                    {t("common.dismiss")}
                  </Chip>
                </Group>
              </Group>
              <Box
                pt={4}
                px={12}
                pb={12}
                style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
              >
                <FieldDiff before={row.parsed} after={row.expected} />
                <Text fz={12} mt={6} c="dimmed">
                  {t("maintenancePage.diffLegend")}
                </Text>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
      <FeedbackLine role="alert">{error}</FeedbackLine>
    </Stack>
  );
}

/**
 * What is currently wrong, as far as one round of cheap queries can tell.
 *
 * Passing checks stay on screen rather than disappearing, because "nothing is wrong" and
 * "that check stopped running" have to look different. A finding offers its repair only when
 * the build actually has that job — a button pointing at a repair that does not exist is
 * worse than no button.
 */
export function Health({
  blocked,
  onStarted,
}: {
  blocked: string | null;
  onStarted: (id: number) => void;
}) {
  const { data, isLoading } = useChecks();
  const { data: jobs } = useMaintenanceJobs();
  const create = useCreateRun();
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const zone = useZone();
  const { t } = useTranslation("admin");

  // The heading stays put while the checks run: a section that vanishes and comes back is
  // the same "that check stopped running" ambiguity this panel exists to avoid.
  if (isLoading)
    return (
      <Stack gap="sm" mb={26} maw={900}>
        <Title order={3} fz="sm" lh={1.5} mb={4}>
          {t("maintenancePage.healthHeading")}
        </Title>
        <Stack gap={6}>
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} height={44} radius="sm" />
          ))}
        </Stack>
      </Stack>
    );
  if (!data) return null;

  const known = new Set((jobs ?? []).map((job) => job.kind));

  async function repair(kind: string) {
    setError(null);
    try {
      const run = await create.mutateAsync({ kind });
      onStarted(run.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  return (
    <Stack gap="sm" mb={26} maw={900}>
      <Title order={3} fz="sm" lh={1.5} mb={4}>
        {t("maintenancePage.healthHeading")}
      </Title>
      {/* One finding open at a time: two sets of samples side by side is two things to hold
          in your head, and the second one is always the one that gets skimmed. */}
      <Accordion
        value={open}
        onChange={setOpen}
        style={{ display: "flex", flexDirection: "column", gap: 6 }}
      >
        {data.findings.map((finding) => {
          const canRepair = finding.repair_kind !== null && known.has(finding.repair_kind);
          // Severity picks both the left rail and the count's ink; they are decided together
          // here so a new severity cannot colour one and quietly miss the other.
          const rail =
            finding.severity === "warn"
              ? "var(--mantine-color-warn-filled)"
              : finding.severity === "error"
                ? "var(--mantine-color-danger-filled)"
                : "var(--mantine-color-default-border)";
          const countColour =
            finding.severity === "warn"
              ? "warn"
              : finding.severity === "error"
                ? "danger"
                : "dimmed";
          return (
            <Accordion.Item
              key={finding.id}
              value={finding.id}
              bg="var(--mantine-color-default)"
              px={12}
              py={8}
              style={{
                border: "1px solid var(--mantine-color-default-border)",
                borderRadius: "var(--mantine-radius-md)",
                borderLeftWidth: 3,
                borderLeftColor: rail,
              }}
            >
              <Group gap={12}>
                <Accordion.Control
                  disabled={finding.samples.length === 0}
                  style={{ flex: "1 1 240px", minWidth: 0 }}
                >
                  <Group gap={10} align="baseline" wrap="nowrap">
                    <Text
                      component="span"
                      size="sm"
                      ta="right"
                      c={countColour}
                      style={{
                        fontVariantNumeric: "tabular-nums",
                        flexShrink: 0,
                        minWidth: "3.5ch",
                      }}
                    >
                      {finding.count}
                    </Text>
                    <Text component="span" fz={13}>
                      {finding.title}
                    </Text>
                  </Group>
                </Accordion.Control>
                {canRepair && (
                  <Chip
                    disabled={blocked !== null || create.isPending}
                    onClick={() => void repair(finding.repair_kind!)}
                  >
                    {t("maintenancePage.previewRepair")}
                  </Chip>
                )}
              </Group>
              <Text fz={12} mt={6} c="dimmed">
                {finding.detail}
              </Text>
              <Accordion.Panel>
                <Box
                  pt={4}
                  px={12}
                  pb={12}
                  style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
                >
                  {finding.samples.map((sample, index) => (
                    <Group
                      key={index}
                      gap={10}
                      py={6}
                      fz={12}
                      align="stretch"
                      wrap="nowrap"
                      style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}
                    >
                      <FieldDiff before={null} after={sample} />
                    </Group>
                  ))}
                </Box>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
      <FeedbackLine role="alert">{error}</FeedbackLine>
      {data.findings[0] && (
        <Text fz={12} mt={6} c="dimmed">
          {t("maintenancePage.checkedAt", {
            when: msToZonedText(Date.parse(data.findings[0].computed_at), zone.name),
          })}
        </Text>
      )}
    </Stack>
  );
}
